import { DOCUMENT, inject, Injectable } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { SITE } from "../constants/site";

export interface SeoConfig {
  title: string;
  description: string;
  /** Path only, for example "/pricing". Combined with the site domain for the canonical URL. */
  path?: string;
  keywords?: string;
  /** Structured data objects rendered as JSON-LD script tags. */
  structuredData?: Record<string, unknown>[];
  /** Social share image path or absolute URL. */
  image?: string;
  /** Alternative text for the social share image. */
  imageAlt?: string;
  /** Open Graph object type. Defaults to "website". */
  type?: string;
  robots?: string;
}

const JSON_LD_ATTR = "data-seo-jsonld";
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const DEFAULT_IMAGE = "/og-image.png";
const DEFAULT_IMAGE_ALT = "VerifyDeceased by LeadCruncher, deceased consumer verification";

@Injectable({ providedIn: "root" })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  apply(config: SeoConfig): void {
    const fullTitle = config.title.includes(SITE.name)
      ? config.title
      : `${config.title} | ${SITE.name}`;
    const canonical = `${SITE.domain}${config.path ?? ""}`;
    const image = this.toAbsolute(config.image ?? DEFAULT_IMAGE);
    const imageAlt = config.imageAlt ?? DEFAULT_IMAGE_ALT;

    this.title.setTitle(fullTitle);

    this.setName("description", config.description);
    this.setName("robots", config.robots ?? DEFAULT_ROBOTS);
    if (config.keywords) {
      this.setName("keywords", config.keywords);
    }

    this.setProperty("og:title", fullTitle);
    this.setProperty("og:description", config.description);
    this.setProperty("og:type", config.type ?? "website");
    this.setProperty("og:url", canonical);
    this.setProperty("og:site_name", SITE.name);
    this.setProperty("og:locale", "en_US");
    this.setProperty("og:image", image);
    this.setProperty("og:image:secure_url", image);
    this.setProperty("og:image:type", image.endsWith(".jpg") ? "image/jpeg" : "image/png");
    this.setProperty("og:image:width", "1200");
    this.setProperty("og:image:height", "630");
    this.setProperty("og:image:alt", imageAlt);

    this.setName("twitter:card", "summary_large_image");
    this.setName("twitter:site", SITE.twitterHandle);
    this.setName("twitter:creator", SITE.twitterHandle);
    this.setName("twitter:title", fullTitle);
    this.setName("twitter:description", config.description);
    this.setName("twitter:image", image);
    this.setName("twitter:image:alt", imageAlt);

    this.setCanonical(canonical);
    this.setAlternates(canonical);
    this.setStructuredData(config.structuredData ?? []);
  }

  private setName(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }

  private setProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private setCanonical(url: string): void {
    this.setLink('link[rel="canonical"]', { rel: "canonical", href: url });
  }

  private setAlternates(url: string): void {
    this.setLink('link[rel="alternate"][hreflang="en-US"]', {
      rel: "alternate",
      hreflang: "en-US",
      href: url,
    });
    this.setLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: url,
    });
  }

  private setLink(selector: string, attributes: Record<string, string>): void {
    let link = this.document.head.querySelector<HTMLLinkElement>(selector);
    if (!link) {
      link = this.document.createElement("link");
      this.document.head.appendChild(link);
    }
    for (const [key, value] of Object.entries(attributes)) {
      link.setAttribute(key, value);
    }
  }

  private setStructuredData(data: Record<string, unknown>[]): void {
    const existing = this.document.head.querySelectorAll(`script[${JSON_LD_ATTR}]`);
    existing.forEach(node => node.remove());

    for (const entry of data) {
      const script = this.document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute(JSON_LD_ATTR, "true");
      script.textContent = JSON.stringify(entry);
      this.document.head.appendChild(script);
    }
  }

  private toAbsolute(pathOrUrl: string): string {
    if (pathOrUrl.startsWith("http")) {
      return pathOrUrl;
    }
    return `${SITE.domain}${pathOrUrl}`;
  }
}
